/**
 * Button Component - Foundational Primitive #6
 *
 * This component uses CSS primitive classes from 06-buttons.css for consistent
 * styling across all button variants. The CSS variables are injected at runtime
 * by themeLoader.ts, enabling zero-code-change theming.
 *
 * Variants:
 * - primary: Filled maroon button (.btn--primary)
 * - secondary: Outlined maroon button (.btn--secondary)
 * - cta: Filled gold button (.btn--cta)
 * - outline/ghost/danger: Legacy variants (backward compatibility)
 * - card: Special card-style button
 *
 * All buttons use sharp corners (border-radius: 0) per DO THIS/NOT THAT guidelines.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../contexts/ThemeContext';
import { useThemeClasses, cn } from '../../hooks/useThemeClasses';
import LoadingSpinner from './LoadingSpinner';
import '../../theme-components/06-buttons.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger' | 'cta' | 'counter' | 'feature' | 'card';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'card' | 'card-large';
  shape?: 'rectangle' | 'rounded' | 'pill' | 'circle';
  isLoading?: boolean;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  fullWidth?: boolean;
  elevation?: boolean; // For shadow effects
  isSelected?: boolean; // For card variant selection state
  children: React.ReactNode;
}

const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  size = 'md',
  shape = 'rectangle',
  isLoading = false,
  leftIcon,
  rightIcon,
  fullWidth = false,
  elevation = false,
  isSelected = false,
  disabled,
  children,
  className = '',
  style = {},
  onMouseEnter,
  onMouseLeave,
  ...props
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const tc = useThemeClasses();
  
  // Map size to Tailwind classes
  const getSizeClasses = (size: string) => {
    const sizeMap = {
      xs: tc.buttonSize.xs,
      sm: tc.buttonSize.sm,
      md: tc.buttonSize.md,
      lg: tc.buttonSize.lg,
      xl: tc.buttonSize.xl,
      card: 'p-6 text-base min-h-[140px] gap-0',
      'card-large': 'p-8 text-base min-h-[300px] gap-0'
    };
    return sizeMap[size as keyof typeof sizeMap] || sizeMap.md;
  };

  // Map variant to CSS primitive classes (from 06-buttons.css) + legacy theme classes
  const getVariantClasses = (variant: string) => {
    const variantMap = {
      // CSS Primitive classes (from 06-buttons.css) - PREFERRED
      primary: cn('btn btn--primary', tc.button.primary),
      secondary: cn('btn btn--secondary', tc.button.secondary),
      cta: cn('btn btn--cta', tc.button.send),

      // Legacy variants (kept for backward compatibility)
      outline: tc.button.outline,
      ghost: tc.button.ghost,
      danger: tc.button.danger,
      counter: `${tc.button.primary} font-bold hover:scale-110`,
      feature: tc.button.secondary,
      card: cn(
        tc.card.base,
        'cursor-pointer border-2 flex-col',
        isSelected ? tc.card.selected : 'hover:shadow-medium hover:scale-105'
      )
    };
    return variantMap[variant as keyof typeof variantMap] || cn('btn btn--primary', variantMap.primary);
  };

  // Map shape to Tailwind classes
  const getShapeClasses = (shape: string) => {
    return tc.buttonShape[shape as keyof typeof tc.buttonShape] || tc.buttonShape.rectangle;
  };

  // Handle mouse events (hover effects are now in CSS)
  const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
    onMouseEnter?.(e);
  };

  const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
    onMouseLeave?.(e);
  };

  // Special classes for circular buttons
  const getCircularClasses = () => {
    if (shape === 'circle') {
      return 'w-10 h-10 min-w-[2.5rem] min-h-[2.5rem] max-w-[2.5rem] max-h-[2.5rem] p-0 text-xl font-bold leading-none';
    }
    return '';
  };

  // Combine all Tailwind classes
  const buttonClasses = cn(
    // Base button classes
    'inline-flex items-center justify-center transition-all duration-300 focus:outline-none',
    'focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed',
    
    // Variant classes
    getVariantClasses(variant),
    
    // Size classes
    getSizeClasses(size),
    
    // Shape classes  
    getShapeClasses(shape),
    
    // Circular button overrides
    getCircularClasses(),
    
    // Layout adjustments
    variant === 'card' ? 'flex-col' : 'flex-row',
    shape === 'circle' ? 'gap-0' : 'gap-2',
    
    // Width
    fullWidth ? 'w-full' : shape === 'circle' ? 'w-10' : 'w-auto',
    
    // Elevation
    elevation && 'shadow-glow',
    
    // Loading/disabled cursor
    (disabled || isLoading) ? 'cursor-not-allowed' : 'cursor-pointer',
    
    // Custom classes
    className
  );

  return (
    <button
      type="button"
      {...props}
      disabled={disabled || isLoading}
      className={buttonClasses}
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {isLoading ? (
        <div className="flex items-center">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          {shape !== 'circle' && <span className="ml-2">{t('forms.loading')}</span>}
        </div>
      ) : (
        <>
          {leftIcon && <span className={shape === 'circle' ? '' : 'mr-2'}>{leftIcon}</span>}
          {children}
          {rightIcon && <span className={shape === 'circle' ? '' : 'ml-2'}>{rightIcon}</span>}
        </>
      )}
    </button>
  );
};

export default Button;